import { z } from 'zod';

// Log level definition
export const LogLevel = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error'
} as const;

type LogLevel = typeof LogLevel[keyof typeof LogLevel];

// Log entry schema
const logEntrySchema = z.object({
  timestamp: z.date(),
  level: z.enum(['debug', 'info', 'warn', 'error']),
  message: z.string(),
  data: z.any().optional(),
  source: z.string().optional()
});

export type LogEntry = z.infer<typeof logEntrySchema>;

class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private subscribers: ((entry: LogEntry) => void)[] = [];
  private maxLogs: number = 1000;

  private constructor() {
    // Override console methods
    const originalConsole = { ...console };
    
    console.debug = (...args) => {
      this.debug(args[0], args.slice(1));
      originalConsole.debug(...args);
    };
    
    console.log = (...args) => {
      this.info(args[0], args.slice(1));
      originalConsole.log(...args);
    };
    
    console.info = (...args) => {
      this.info(args[0], args.slice(1));
      originalConsole.info(...args);
    };
    
    console.warn = (...args) => {
      this.warn(args[0], args.slice(1));
      originalConsole.warn(...args);
    };
    
    console.error = (...args) => {
      this.error(args[0], args.slice(1));
      originalConsole.error(...args);
    };
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private addLog(level: LogLevel, message: string, data?: any, source?: string) {
    const entry = logEntrySchema.parse({
      timestamp: new Date(),
      level,
      message,
      data,
      source
    });

    this.logs.push(entry);
    
    // Trim logs if exceeding max size
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Notify subscribers
    this.subscribers.forEach(subscriber => subscriber(entry));
  }

  public debug(message: string, data?: any, source?: string) {
    this.addLog(LogLevel.DEBUG, message, data, source);
  }

  public info(message: string, data?: any, source?: string) {
    this.addLog(LogLevel.INFO, message, data, source);
  }

  public warn(message: string, data?: any, source?: string) {
    this.addLog(LogLevel.WARN, message, data, source);
  }

  public error(message: string, data?: any, source?: string) {
    this.addLog(LogLevel.ERROR, message, data, source);
  }

  public getLogs(): LogEntry[] {
    return [...this.logs];
  }

  public subscribe(callback: (entry: LogEntry) => void) {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback);
    };
  }

  public clear() {
    this.logs = [];
    this.subscribers.forEach(subscriber => 
      subscriber({
        timestamp: new Date(),
        level: 'info',
        message: 'Logs cleared'
      })
    );
  }
}

export const logger = Logger.getInstance();